import React, { useState, useEffect, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSync,
  faDatabase,
  faDownload,
  faTrashAlt,
  faCheckCircle,
  faTimesCircle,
  faSpinner,
  faServer,
  faCloud,
  faClock,
  faExclamationTriangle,
  faInfoCircle,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import "../../css/Common.css";
import "./css/FccCacheAdmin.css";
import AdminFccCacheAPI from "../../services/AdminFccCacheAPI";
import LoadingSpinner from "../../common/LoadingSpinner";
import toast from "react-hot-toast";
import { useTranslation } from "../../../../i18n/LanguageContext";

const PURGE_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 60, label: "60 days" },
  { value: 90, label: "90 days" },
  { value: 120, label: "120 days" },
  { value: 180, label: "180 days" },
];

function FccCacheAdmin() {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [fullDownload, setFullDownload] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeAge, setPurgeAge] = useState(90);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showPurgeConfirm, setShowPurgeConfirm] = useState(false);

  // Load cache statistics
  const loadStats = useCallback(async () => {
    try {
      const response = await AdminFccCacheAPI.getCacheStats();
      if (response.success) {
        setStats(response.data);
      } else {
        toast.error(response.message || "Failed to load FCC cache stats");
      }
      setLastUpdated(new Date());
    } catch (error) {
      console.error("[FccCacheAdmin] Error loading stats:", error);
      toast.error(t("fccCache.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Auto-refresh every 30 seconds while a sync is in progress
  useEffect(() => {
    if (!stats?.syncInProgress) return;

    const interval = setInterval(() => {
      loadStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [stats?.syncInProgress, loadStats]);

  // Trigger bulk download
  const handleBulkDownload = async () => {
    try {
      setDownloading(true);
      const response = await AdminFccCacheAPI.triggerBulkDownload(fullDownload);
      if (response.success) {
        toast.success(
          "FCC bulk download started. This may take several minutes.",
        );
        // Refresh stats after a short delay to pick up in-progress status
        setTimeout(() => loadStats(), 3000);
      } else {
        toast.error(response.message || "Failed to start bulk download");
      }
    } catch (error) {
      console.error("[FccCacheAdmin] Bulk download error:", error);
      toast.error(
        "Failed to trigger bulk download: " +
          (error.message || "Unknown error"),
      );
    } finally {
      setDownloading(false);
    }
  };

  // Purge stale entries
  const handlePurge = async () => {
    try {
      setPurging(true);
      setShowPurgeConfirm(false);
      const response = await AdminFccCacheAPI.purgeStaleEntries(purgeAge);
      if (response.success) {
        const deleted = response.data?.deletedRecords || 0;
        toast.success(
          t("fccCache.toast.purged").replace(
            "{count}",
            deleted.toLocaleString(),
          ),
        );
        await loadStats();
      } else {
        toast.error(response.message || "Failed to purge cache");
      }
    } catch (error) {
      console.error("[FccCacheAdmin] Purge error:", error);
      toast.error(
        "Failed to purge cache: " + (error.message || "Unknown error"),
      );
    } finally {
      setPurging(false);
    }
  };

  // Format numbers
  const formatNumber = (num) => {
    if (num == null) return "0";
    return Number(num).toLocaleString();
  };

  // Format last sync display
  const formatLastSync = (syncValue) => {
    if (!syncValue || syncValue === "Never") return "Never";
    try {
      const date = new Date(syncValue);
      return date.toLocaleString();
    } catch {
      return syncValue;
    }
  };

  // Time since last sync
  const getTimeSinceSync = (syncValue) => {
    if (!syncValue || syncValue === "Never") return null;
    try {
      const syncDate = new Date(syncValue);
      const now = new Date();
      const diffMs = now - syncDate;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffHours / 24);

      if (diffDays > 0)
        return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
      if (diffHours > 0)
        return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
      return "Less than an hour ago";
    } catch {
      return null;
    }
  };

  // Freshness indicator
  const getSyncFreshness = (syncValue) => {
    if (!syncValue || syncValue === "Never")
      return { color: "#ef4444", label: "No data" };
    try {
      const syncDate = new Date(syncValue);
      const now = new Date();
      const diffDays = Math.floor((now - syncDate) / (1000 * 60 * 60 * 24));
      if (diffDays <= 7) return { color: "#10b981", label: "Fresh" };
      if (diffDays <= 14) return { color: "#f59e0b", label: "Aging" };
      return { color: "#ef4444", label: "Stale" };
    } catch {
      return { color: "#6b7280", label: "Unknown" };
    }
  };

  if (loading) {
    return (
      <div className="fcc-cache-admin">
        <LoadingSpinner message="Loading FCC cache statistics..." />
      </div>
    );
  }

  const freshness = getSyncFreshness(stats?.lastBulkSync);
  const timeSince = getTimeSinceSync(stats?.lastBulkSync);

  return (
    <div className="fcc-cache-admin">
      {/* Page Header */}
      <div className="fcc-page-header">
        <div className="fcc-header-info">
          <h2>
            <FontAwesomeIcon icon={faShieldAlt} className="header-icon" />
            FCC Complaint Cache
          </h2>
          <p>
            Manage the local cache of FCC Consumer Complaint Database records
            used for phone number scam detection.
          </p>
        </div>
        <div className="fcc-header-controls">
          <button
            className="fcc-refresh-btn"
            onClick={() => {
              setLoading(true);
              loadStats();
            }}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faSync} spin={loading} />
            Refresh
          </button>
          {lastUpdated && (
            <span className="fcc-last-updated">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
        </div>
      </div>

      {/* Sync In Progress Banner */}
      {stats?.syncInProgress && (
        <div className="fcc-sync-banner">
          <FontAwesomeIcon icon={faSpinner} spin className="banner-icon" />
          <div className="banner-content">
            <strong>Bulk download in progress</strong>
            <span>
              Records are being downloaded from the FCC database. Stats will
              auto-refresh every 30 seconds.
            </span>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="fcc-kpi-grid">
        <div className="fcc-kpi-card total-records">
          <div className="kpi-icon">
            <FontAwesomeIcon icon={faDatabase} />
          </div>
          <div className="kpi-value">{formatNumber(stats?.totalRecords)}</div>
          <div className="kpi-label">Total Cached Records</div>
        </div>

        <div className="fcc-kpi-card api-sourced">
          <div className="kpi-icon">
            <FontAwesomeIcon icon={faCloud} />
          </div>
          <div className="kpi-value">{formatNumber(stats?.apiSourced)}</div>
          <div className="kpi-label">API-Sourced (On-Demand)</div>
        </div>

        <div className="fcc-kpi-card bulk-sourced">
          <div className="kpi-icon">
            <FontAwesomeIcon icon={faServer} />
          </div>
          <div className="kpi-value">{formatNumber(stats?.bulkSourced)}</div>
          <div className="kpi-label">Bulk-Downloaded</div>
        </div>

        <div className="fcc-kpi-card last-sync">
          <div className="kpi-icon">
            <FontAwesomeIcon icon={faClock} />
          </div>
          <div className="kpi-value" style={{ fontSize: "1.3rem" }}>
            {timeSince || "Never"}
          </div>
          <div className="kpi-label">Last Bulk Sync</div>
          <div
            className="kpi-freshness-badge"
            style={{ backgroundColor: freshness.color }}
          >
            {freshness.label}
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="fcc-actions-grid">
        {/* Bulk Download Card */}
        <div className="fcc-action-card">
          <div className="action-card-header">
            <FontAwesomeIcon
              icon={faDownload}
              className="action-icon download"
            />
            <div>
              <h3>Bulk Download</h3>
              <p>
                Download FCC Consumer Complaint records from the FCC SODA API.
                By default, downloads only new records since the last sync.
                Check "Full Download" to re-download everything.
              </p>
            </div>
          </div>
          <div className="action-card-details">
            <div className="detail-row">
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>
                Last bulk sync:{" "}
                <strong>{formatLastSync(stats?.lastBulkSync)}</strong>
              </span>
            </div>
            <div className="detail-row">
              <FontAwesomeIcon icon={faInfoCircle} />
              <span>
                Last incremental update:{" "}
                <strong>{formatLastSync(stats?.lastIncrementalSync)}</strong>
              </span>
            </div>
            <div className="detail-row">
              <FontAwesomeIcon icon={faDatabase} />
              <span>
                Current bulk records:{" "}
                <strong>{formatNumber(stats?.bulkSourced)}</strong>
              </span>
            </div>
            <div className="detail-row">
              <FontAwesomeIcon icon={faDownload} />
              <span>
                Last incremental records downloaded:{" "}
                <strong>{formatNumber(stats?.lastIncrementalRecords)}</strong>
              </span>
            </div>
          </div>
          <div className="fcc-download-options">
            <label className="fcc-checkbox-label">
              <input
                type="checkbox"
                checked={fullDownload}
                onChange={(e) => setFullDownload(e.target.checked)}
                disabled={downloading || stats?.syncInProgress}
              />
              <span className="fcc-checkbox-text">Full Download</span>
            </label>
            <span className="fcc-checkbox-hint">
              {fullDownload
                ? "Downloads ALL records (ignores last sync date)"
                : "Downloads only new records since last sync (incremental)"}
            </span>
          </div>
          <button
            className="fcc-action-btn download-btn"
            onClick={handleBulkDownload}
            disabled={downloading || stats?.syncInProgress}
          >
            {downloading || stats?.syncInProgress ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin />
                {stats?.syncInProgress
                  ? "Download In Progress..."
                  : "Starting..."}
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faDownload} />
                Start Bulk Download
              </>
            )}
          </button>
          {stats?.syncInProgress && (
            <div className="action-card-note">
              <FontAwesomeIcon icon={faExclamationTriangle} />A download is
              already running. Please wait for it to complete.
            </div>
          )}
        </div>

        {/* Purge Cache Card */}
        <div className="fcc-action-card">
          <div className="action-card-header">
            <FontAwesomeIcon icon={faTrashAlt} className="action-icon purge" />
            <div>
              <h3>Purge Stale Entries</h3>
              <p>
                Remove old cache entries to keep the database lean. Only affects
                records older than the selected threshold.
              </p>
            </div>
          </div>
          <div className="action-card-details">
            <div className="detail-row">
              <FontAwesomeIcon icon={faClock} />
              <span>Purge entries older than:</span>
            </div>
            <div className="purge-age-selector">
              {PURGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`purge-age-btn ${purgeAge === opt.value ? "active" : ""}`}
                  onClick={() => setPurgeAge(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {!showPurgeConfirm ? (
            <button
              className="fcc-action-btn purge-btn"
              onClick={() => setShowPurgeConfirm(true)}
              disabled={purging || stats?.syncInProgress}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
              Purge Stale Entries
            </button>
          ) : (
            <div className="purge-confirm">
              <div className="purge-confirm-message">
                <FontAwesomeIcon icon={faExclamationTriangle} />
                <span>
                  Are you sure? This will permanently delete all cached FCC
                  records older than <strong>{purgeAge} days</strong>.
                </span>
              </div>
              <div className="purge-confirm-buttons">
                <button
                  className="fcc-action-btn purge-btn confirm"
                  onClick={handlePurge}
                  disabled={purging}
                >
                  {purging ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} spin />
                      Purging...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCheckCircle} />
                      Yes, Purge
                    </>
                  )}
                </button>
                <button
                  className="fcc-action-btn cancel-btn"
                  onClick={() => setShowPurgeConfirm(false)}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Info Footer */}
      <div className="fcc-info-footer">
        <FontAwesomeIcon icon={faInfoCircle} />
        <div>
          <strong>How it works:</strong> Phone numbers checked by users are
          first looked up in this local cache (7-day freshness window). Cache
          misses query the FCC SODA API in real-time and persist the result.
          Bulk downloads pre-populate the cache for faster lookups. A scheduled
          job runs weekly (Sundays at 2 AM) when enabled.
        </div>
      </div>
    </div>
  );
}

export default FccCacheAdmin;
